import { useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { APP_API_V1_BASE } from "../../../config/api";
import { useNotification } from "../../../context/NotificationContext";

const STATUS_OPTIONS = [
  "PENDING",
  "DOWNLOADING",
  "INSTALLING",
  "INSTALLED",
  "FAILED",
  "CANCELED",
];

export default function FirmwareDiagnosticsPanel({
  deviceId,
  activeOrgId,
  canRunChecks,
  canSubmitStatus,
}) {
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkError, setCheckError] = useState("");
  const [checkResult, setCheckResult] = useState(null);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [statusError, setStatusError] = useState("");
  const { notification, showNotification, closeNotification } = useNotification();
  //const [statusSuccess, setStatusSuccess] = useState("");
  const [statusForm, setStatusForm] = useState({
    deployment_id: "",      
    status: "PENDING",
    current_version: "",
    error_message: "",
  });

  const downloadUrl = checkResult?.firmware?.download_url || "";

  const getAuthHeaders = () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      throw new Error("AUTH_TOKEN_MISSING");
    }
    return {
      Authorization: `Bearer ${token}`,
      ...(activeOrgId && { "X-Org-ID": activeOrgId }),
    };
  };

  const runFirmwareCheck = async () => {
    if (!deviceId || !canRunChecks) return;
    try {
      setCheckLoading(true);
      setCheckError("");
      const res = await axios.get(
        `${APP_API_V1_BASE}/devices/${deviceId}/firmware/check`,
        {
          headers: getAuthHeaders(),
        },
      );
      setCheckResult(res.data || null);
      if (res.data?.deployment_id) {
        setStatusForm((prev) => ({
          ...prev,
          deployment_id: prev.deployment_id || res.data.deployment_id,
          current_version:
            prev.current_version || res.data?.firmware?.version || "",
        }));
      }
    } catch (error) {
      setCheckResult(null);
      setCheckError(
        error?.response?.data?.error ||
          error?.message ||
          "Failed to run firmware check.",
      );
    } finally {
      setCheckLoading(false);
    }
  };

  const submitStatusUpdate = async () => {
    if (!deviceId || !canSubmitStatus) return;
    if (!statusForm.deployment_id || !statusForm.status) {
      setStatusError("Deployment ID and status are required.");
      return;
    }
    try {
      setStatusSubmitting(true);
      setStatusError("");
      //setStatusSuccess("");
      const payload = {
        deployment_id: statusForm.deployment_id.trim(),
        status: statusForm.status,
        current_version: statusForm.current_version || null,
        error_message: statusForm.error_message || null,
      };
      const res = await axios.post(
        `${APP_API_V1_BASE}/devices/${deviceId}/firmware/status`,
        payload,
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        },
      );
      showNotification(
  res?.data?.message || "Firmware status submitted.",
  "success"
);
    } catch (error) {
      setStatusError(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Failed to submit firmware status.",
      );
    } finally {
      setStatusSubmitting(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        border: "1px solid rgba(226,232,240,0.95)",
        borderRadius: 4,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.2,
          borderBottom: "1px solid rgba(226,232,240,0.95)",
          background: "linear-gradient(180deg, #F8FAFC, #F1F5F9)",
        }}
      >
        <Typography sx={{ fontWeight: 800, color: "#0F172A" }}>
          OTA Diagnostics (Admin)
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        {!canRunChecks ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            OTA diagnostics are visible only to platform administrators.
          </Alert>
        ) : (
          <>
            {checkError ? (
              <Alert severity="error" sx={{ borderRadius: 2, mb: 1.5 }}>
                {checkError}
              </Alert>
            ) : null}

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ mb: 1.5 }}
            >
              <Button
                variant="contained"
                startIcon={<PlayArrowRoundedIcon />}
                onClick={runFirmwareCheck}
                disabled={checkLoading}
                sx={{ textTransform: "none" }}
              >
                {checkLoading ? "Running Check..." : "Run Firmware Check"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<OpenInNewRoundedIcon />}
                onClick={() => window.open(downloadUrl, "_blank", "noopener,noreferrer")}
                disabled={!downloadUrl}
                sx={{ textTransform: "none" }}
              >
                Open Download URL
              </Button>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
              <Chip
                size="small"
                color={checkResult?.update_available ? "success" : "default"}
                label={
                  checkResult
                    ? checkResult.update_available
                      ? "Update Available"
                      : "No Update"
                    : "Not Checked"
                }
              />
              {checkResult?.caller && (
                <Chip size="small" variant="outlined" label={`Caller: ${checkResult.caller}`} />
              )}
            </Stack>

            {checkResult ? (
              <Box
                sx={{
                  border: "1px solid rgba(226,232,240,0.9)",
                  borderRadius: 2,
                  p: 1.5,
                  background: "#FCFDFE",
                }}
              >
                <Typography fontSize={12} color="#64748B" fontWeight={700}>
                  Deployment ID
                </Typography>
                <Typography fontSize={13} sx={{ fontFamily: "monospace", mb: 1 }}>
                  {checkResult?.deployment_id || "--"}
                </Typography>
                <Typography fontSize={12} color="#64748B" fontWeight={700}>
                  Firmware
                </Typography>
                <Typography fontSize={13} sx={{ mb: 0.5 }}>
                  {(checkResult?.firmware?.firmware_id || "--") +
                    " · v" +
                    (checkResult?.firmware?.version || "--")}
                </Typography>
                {/* Proxy token diagnostics intentionally hidden for now */}
              </Box>
            ) : null}

            <Divider sx={{ my: 2 }} />

            <Typography sx={{ fontWeight: 700, color: "#0F172A", mb: 1 }}>
              QA Status Update
            </Typography>
            {statusError ? (
              <Alert severity="error" sx={{ borderRadius: 2, mb: 1.5 }}>
                {statusError}
              </Alert>
            ) : null}
            {/* {statusSuccess ? (
              <Alert severity="success" sx={{ borderRadius: 2, mb: 1.5 }}>
                {statusSuccess}
              </Alert>
            ) : null} */}
            {notification.open ? (
  <Alert
    severity={notification.severity}
    sx={{ borderRadius: 2, mb: 1.5 }}
    onClose={closeNotification}
  >
    {notification.message}
  </Alert>
) : null}

            <Stack spacing={1.2}>
              <TextField
                size="small"
                label="Deployment ID"
                value={statusForm.deployment_id}
                onChange={(e) =>
                  setStatusForm((prev) => ({
                    ...prev,
                    deployment_id: e.target.value,
                  }))
                }
                disabled={!canSubmitStatus}
              />
              <TextField
                select
                size="small"
                label="Status"
                value={statusForm.status}
                onChange={(e) =>
                  setStatusForm((prev) => ({
                    ...prev,
                    status: e.target.value,
                  }))
                }
                disabled={!canSubmitStatus}
              >
                {STATUS_OPTIONS.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Current Version"
                value={statusForm.current_version}
                onChange={(e) =>
                  setStatusForm((prev) => ({
                    ...prev,
                    current_version: e.target.value,
                  }))
                }
                disabled={!canSubmitStatus}
              />
              <TextField
                size="small"
                label="Error Message"
                multiline
                minRows={2}
                value={statusForm.error_message}
                onChange={(e) =>
                  setStatusForm((prev) => ({
                    ...prev,
                    error_message: e.target.value,
                  }))
                }
                disabled={!canSubmitStatus}
              />
              <Button
                variant="contained"
                startIcon={<SendRoundedIcon />}
                onClick={submitStatusUpdate}
                disabled={!canSubmitStatus || statusSubmitting}
                sx={{ textTransform: "none", alignSelf: "flex-start" }}
              >
                {statusSubmitting ? "Submitting..." : "Submit Status"}
              </Button>
            </Stack>
          </>
        )}
      </Box>
    </Paper>
  );
}
